import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Alert, Box, Button, Stack } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { positionRequestDecisionUrl, positionRequestQueueUrl } from 'api/positionChangeRequest';
import { PositionChangeDecisionModal } from 'components/PositionChangeDecisionModal';
import { useNotifications } from 'contexts/notification-context';
import { positionChangeRequestColumns } from 'utils/dataColumns/positionChangeRequestColumns';
import { getEmployeeId } from 'utils/auth';
import useHttp from 'hooks/useHttp';

export const PositionChangeRequestsTable = ({ search, onChanged }) => {
    const { sendRequest } = useHttp();
    const send = useRef(sendRequest);
    send.current = sendRequest;
    const {
        positionRequestsRevision, refreshPositionRequests, pendingPositionCountError, refreshPendingCount,
    } = useNotifications();
    const [pagination, setPagination] = useState({ page: 0, pageSize: 25 });
    const [rows, setRows] = useState([]);
    const [rowCount, setRowCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [readError, setReadError] = useState('');
    const [revision, setRevision] = useState(0);
    const [decision, setDecision] = useState(null);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState('');
    const [feedback, setFeedback] = useState(null);
    const busy = useRef(false);
    const mounted = useRef(false);
    const sequence = useRef(0);
    const readController = useRef(null);
    const viewerId = getEmployeeId();

    useEffect(() => {
        mounted.current = true;
        const onFocus = () => { if (!busy.current) { setRevision((value) => value + 1); } };
        window.addEventListener('focus', onFocus);
        return () => {
            mounted.current = false;
            sequence.current++;
            readController.current?.abort();
            window.removeEventListener('focus', onFocus);
        };
    }, []);

    useEffect(() => {
        const current = ++sequence.current;
        const controller = new AbortController();
        readController.current = controller;
        setLoading(true);
        (async () => {
            const result = await send.current({
                url: positionRequestQueueUrl(pagination, search), signal: controller.signal,
            });
            if (!mounted.current || current !== sequence.current || getEmployeeId() !== viewerId) {
                return;
            }
            setLoading(false);
            if (result.ok && Array.isArray(result.data?.data) && Number.isInteger(result.data.totalCount)) {
                const lastPage = Math.max(0, Math.ceil(result.data.totalCount / pagination.pageSize) - 1);
                if (pagination.page > lastPage) {
                    setPagination((previous) => ({ ...previous, page: lastPage }));
                    return;
                }
                setRows(result.data.data);
                setRowCount(result.data.totalCount);
                setReadError('');
            } else {
                setReadError(result.error?.response?.data?.message || 'Could not load position change requests. Please retry.');
            }
        })();
        return () => { sequence.current++; controller.abort(); };
    }, [pagination, search, revision, positionRequestsRevision, viewerId]);

    const openDecision = useCallback((request, action) => {
        setSaveError('');
        setFeedback(null);
        setDecision({ request, action });
    }, []);
    const columns = useMemo(() => positionChangeRequestColumns(openDecision, loading || saving || !!readError),
        [openDecision, loading, saving, readError]);

    const confirm = async (reason) => {
        if (busy.current || !decision || getEmployeeId() !== viewerId) {
            return;
        }
        busy.current = true;
        setSaving(true);
        setSaveError('');
        sequence.current++;
        readController.current?.abort();
        const { request, action } = decision;
        const result = await send.current({
            url: positionRequestDecisionUrl(request.id, action), method: 'POST',
            data: { version: request.version, ...(action === 'reject' ? { reason } : {}) },
        });
        if (!mounted.current || getEmployeeId() !== viewerId) {
            return;
        }
        busy.current = false;
        setSaving(false);
        const conflict = result.error?.response?.status === 409;
        if (result.ok || conflict) {
            setDecision(null);
            if (result.ok) {
                setRows((previous) => previous.filter((row) => row.id !== request.id));
            }
            setFeedback({
                severity: conflict ? 'warning' : 'success',
                message: conflict ? 'This request changed or was already handled. The list has been refreshed.'
                    : `Position change request ${action === 'approve' ? 'approved' : 'rejected'}.`,
            });
            setRevision((value) => value + 1);
            refreshPositionRequests();
            onChanged();
        } else {
            setSaveError(result.error?.response?.data?.message || 'The decision could not be saved. Please try again.');
            // A read interrupted by this mutation must not leave the table permanently loading.
            setLoading(false);
        }
    };

    return (
        <Stack spacing={2}>
            {pendingPositionCountError && <Alert severity="warning" action={
                <Button color="inherit" onClick={refreshPendingCount}>Retry count</Button>
            }>{pendingPositionCountError}</Alert>}
            {feedback && <Alert severity={feedback.severity}>{feedback.message}</Alert>}
            {readError && <Alert severity="error" action={
                <Button color="inherit" onClick={() => setRevision((value) => value + 1)} disabled={loading}>Retry</Button>
            }>{readError}</Alert>}
            <Box><Button onClick={() => {
                setRevision((value) => value + 1); refreshPendingCount();
            }} disabled={loading || saving}>Refresh requests</Button></Box>
            <DataGrid autoHeight rows={rows} columns={columns} rowCount={rowCount}
                loading={loading} paginationMode="server" paginationModel={pagination}
                onPaginationModelChange={(model) => {
                    if (!busy.current) {
                        setPagination((previous) => ({
                            ...model, page: previous.pageSize !== model.pageSize ? 0 : model.page,
                        }));
                    }
                }}
                pageSizeOptions={[25, 50]} disableColumnFilter disableColumnMenu disableRowSelectionOnClick
                localeText={{ noRowsLabel: readError ? 'Requests could not be loaded.' :
                    search ? 'No matching pending position change requests.' : 'No pending position change requests.' }} />
            {decision && <PositionChangeDecisionModal request={decision.request} action={decision.action}
                saving={saving} error={saveError} onClose={() => { if (!busy.current) { setDecision(null); } }}
                onConfirm={confirm} />}
        </Stack>
    );
};

PositionChangeRequestsTable.propTypes = { search: PropTypes.string.isRequired, onChanged: PropTypes.func.isRequired };
